import { useEffect, useRef, useState } from "react";
import { useMotionValue, animate } from "motion/react";
import { Resizable } from "re-resizable";
import { useLocalStorage } from "@uidotdev/usehooks";
import { ChatPanel } from "./chat-panel";

const COLLAPSED_WIDTH = 0;
const DEFAULT_EXPANDED_WIDTH = 500;
const MIN_WIDTH = 400;
const MAX_WIDTH = 800;

export function ResizableChatPanel({ collapsed }: { collapsed: boolean }) {
  const [localStorageOpenWidth, setLocalStorageOpenWidth] =
    useLocalStorage<number>("chat-panel-open-width", DEFAULT_EXPANDED_WIDTH);

  // Use motion value for width
  const initialWidth = collapsed ? COLLAPSED_WIDTH : localStorageOpenWidth;
  const widthMotionValue = useMotionValue(initialWidth);
  const [currentWidth, setCurrentWidth] = useState(initialWidth);
  const isResizing = useRef(false);
  const animationRef = useRef<ReturnType<typeof animate> | null>(null);
  const resizeStartWidth = useRef(initialWidth);

  // Animate width when panel collapses/expands (only when not resizing)
  useEffect(() => {
    if (isResizing.current) return;

    const targetWidth = collapsed ? COLLAPSED_WIDTH : localStorageOpenWidth;

    // Cancel any ongoing animation
    if (animationRef.current) {
      animationRef.current.stop();
    }

    // Use motion's animate function for smooth transitions
    animationRef.current = animate(widthMotionValue, targetWidth, {
      duration: 0.3,
      ease: "easeInOut",
      onUpdate: (latest) => {
        setCurrentWidth(latest);
      },
      onComplete: () => {
        widthMotionValue.set(targetWidth);
      },
    });

    return () => {
      if (animationRef.current) {
        animationRef.current.stop();
      }
    };
  }, [collapsed, widthMotionValue, localStorageOpenWidth]);

  const handleResize = (
    _event: MouseEvent | TouchEvent,
    _direction: unknown,
    _ref: HTMLElement,
    delta: { height: number; width: number },
  ) => {
    // delta.width is cumulative from the start of resize, not incremental
    const newWidth = resizeStartWidth.current + delta.width;
    widthMotionValue.set(newWidth);
    setCurrentWidth(newWidth);
  };

  const handleResizeStart = () => {
    isResizing.current = true;
    // Store the width at the start of resize
    resizeStartWidth.current = widthMotionValue.get();
    // Stop any ongoing animation
    if (animationRef.current) {
      animationRef.current.stop();
    }
  };

  const handleResizeStop = (
    _event: MouseEvent | TouchEvent,
    _direction: unknown,
    _ref: HTMLElement,
    _delta: { height: number; width: number },
  ) => {
    isResizing.current = false;
    // Save the new width to localStorage
    const finalWidth = widthMotionValue.get();
    setLocalStorageOpenWidth(finalWidth);
  };

  return (
    <Resizable
      size={{
        width: currentWidth,
        height: "100%",
      }}
      onResizeStart={handleResizeStart}
      onResize={handleResize}
      onResizeStop={handleResizeStop}
      minWidth={!collapsed ? MIN_WIDTH : undefined}
      maxWidth={!collapsed ? MAX_WIDTH : undefined}
      enable={{
        top: false,
        right: !collapsed,
        bottom: false,
        left: false,
        topRight: false,
        bottomRight: false,
        bottomLeft: false,
        topLeft: false,
      }}
      className="border-r overflow-hidden h-full"
    >
      {!collapsed && <ChatPanel />}
    </Resizable>
  );
}
